import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  fetchDashboard,
  fetchResourceOccupancy,
  fetchVenueZones,
  type DashboardRow,
  type OccupancyView,
  type Zone,
} from '../api-client.ts';

export interface ZoneWithOccupancy {
  readonly zone: Zone;
  readonly occupancy: OccupancyView | null;
}

export interface DashboardState {
  readonly rows: readonly DashboardRow[];
  readonly zones: readonly ZoneWithOccupancy[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly reload: () => void;
}

export interface UseDashboardParams {
  readonly venueId: string;
  readonly todayIsoDate: string;
  readonly timezone: string;
  readonly windowStartIso: string;
  readonly windowEndIso: string;
}

export function useDashboard(p: UseDashboardParams): DashboardState {
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [zones, setZones] = useState<ZoneWithOccupancy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [list, zoneList] = await Promise.all([
        fetchDashboard({
          venueId: p.venueId,
          today: p.todayIsoDate,
          tz: p.timezone,
        }),
        fetchVenueZones(p.venueId),
      ]);
      const occupancies = await Promise.all(
        zoneList.map((z) =>
          fetchResourceOccupancy({
            resourceId: z.resourceId,
            startTime: p.windowStartIso,
            endTime: p.windowEndIso,
          }).catch(() => null),
        ),
      );
      setRows(list);
      setZones(zoneList.map((zone, idx) => ({ zone, occupancy: occupancies[idx] ?? null })));
    } catch (e) {
      setError(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [p.venueId, p.todayIsoDate, p.timezone, p.windowStartIso, p.windowEndIso]);

  useEffect(() => {
    void fetchAll(false); // initial load: show spinner
    // Auto-refresh every 30s — silent (no spinner flicker) so the list
    // updates in-place. New guest bookings surface without manual reload,
    // which was the root cause of "I booked but staff can't see" UX.
    const iv = setInterval(() => {
      void fetchAll(true);
    }, 30_000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const reload = useCallback(() => {
    void fetchAll(false);
  }, [fetchAll]);

  return { rows, zones, loading, error, reload };
}
